import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import type { SEOHandle } from '@nasa-gcn/remix-seo'
import { animated, useSpring } from '@react-spring/web'
import { Form, Link, data, useSearchParams } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { AuthFormWrapper } from '#app/components/auth/AuthFormWrapper'
import { AuthHeader } from '#app/components/auth/AuthHeader'
import { AuthLayout } from '#app/components/auth/AuthLayout'
import { AuthProviders } from '#app/components/auth/AuthProviders'
import { GeneralErrorBoundary } from '#app/components/error-boundary'
import { ErrorList, FloatingLabelField } from '#app/components/forms'
import { AnimatedContainer } from '#app/components/ui/animated-container'
import { AnimatedStatusButton } from '#app/components/ui/animated-status-button'
import { Checkbox } from '#app/components/ui/checkbox'
import { StaggerChildren } from '#app/components/ui/stagger-children'
import { smoothSpring } from '#app/utils/animations'
import { getAuthConfig } from '#app/utils/auth-config.server'
import { login, requireAnonymous } from '#app/utils/auth.server'
import { checkHoneypot } from '#app/utils/honeypot.server'
import { useIsPending } from '#app/utils/misc'
import { EmailSchema, PasswordSchema } from '#app/utils/user-validation'
import type { Route } from './+types/landlord.login'
import { handleNewSession } from './login.server'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

const LoginFormSchema = z.object({
	username: EmailSchema,
	password: PasswordSchema,
	redirectTo: z.string().optional(),
	remember: z.boolean().optional(),
})

export async function loader({ request }: Route.LoaderArgs) {
	await requireAnonymous(request)
	const authConfig = getAuthConfig('landlord')
	return { authConfig }
}

export async function action({ request }: Route.ActionArgs) {
	await requireAnonymous(request)
	const formData = await request.formData()
	await checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: (intent) =>
			LoginFormSchema.transform(async (data, ctx) => {
				if (intent !== null) return { ...data, session: null }

				try {
					const session = await login({
						...data,
						expectedUserType: 'landlord',
					})
					if (!session) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: 'Invalid email or password',
						})
						return z.NEVER
					}

					return { ...data, session }
				} catch (error) {
					// Handle userType mismatch error
					if (error instanceof Error && error.message.includes('user type')) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message:
								'This account is registered as a tenant. Please use the tenant login page.',
						})
						return z.NEVER
					}
					throw error
				}
			}),
		async: true,
	})

	if (submission.status !== 'success' || !submission.value.session) {
		return data(
			{ result: submission.reply({ hideFields: ['password'] }) },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { session, remember, redirectTo } = submission.value

	return handleNewSession({
		request,
		session,
		remember: remember ?? false,
		redirectTo,
	})
}

export default function LandlordLoginPage({
	actionData,
	loaderData,
}: Route.ComponentProps) {
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')
	const authConfig = loaderData.authConfig

	// Build signup link preserving redirectTo param
	const signupLink = redirectTo
		? `${authConfig.signupPath}?redirectTo=${encodeURIComponent(redirectTo)}`
		: authConfig.signupPath

	const [form, fields] = useForm({
		id: 'login-form',
		constraint: getZodConstraint(LoginFormSchema),
		defaultValue: { redirectTo },
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: LoginFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	// React Spring animation for error messages
	const hasErrors = Boolean(form.errors?.length)
	const errorSpring = useSpring({
		opacity: hasErrors ? 1 : 0,
		y: hasErrors ? 0 : -10,
		config: smoothSpring,
	})

	return (
		<AuthLayout
			backgroundImage={authConfig.backgroundImage}
			heroText={authConfig.heroText}
			userType="landlord"
		>
			<AuthHeader />
			<AuthFormWrapper
				title={authConfig.titles.login}
				subtitle={authConfig.subtitles.login}
				titleClassName="text-[#32302F] text-[22px] md:text-[35px] capitalize font-medium"
				subtitleClassName="text-[#747474] font-400 text-[16px] md:text-[20px] "
			>
				<AnimatedContainer>
					<Form method="POST" {...getFormProps(form)}>
						<HoneypotInputs />
						<StaggerChildren>
							<FloatingLabelField
								inputProps={{
									...getInputProps(fields.username, { type: 'email' }),
									autoFocus: true,
									autoComplete: 'email',
									placeholder: 'Enter Your Email Address',
									className:
										'placeholder:text-[#CCCCCC]  placeholder:text-[16px] text-[16px] px-[33px] py-[24px] text-[#484848] border border-[#CCCCCC] rounded-[30px]',
								}}
								labelProps={{
									children: 'Email',
									htmlFor: fields.username.id,
									className: 'text-[16px] text-[#32302F]',
								}}
								errorClass={'min-h-auto  md:px-0 md:pt-0 md:pb-0 '}
								className="mb-[20px]"
								errors={fields.username.errors}
							/>
							<FloatingLabelField
								passwordHide
								inputProps={{
									...getInputProps(fields.password, { type: 'password' }),
									autoComplete: 'password',
									placeholder: 'Enter Password',
									className:
										'placeholder:text-[#CCCCCC] placeholder:text-[16px] text-[16px] px-[33px] py-[24px] border border-[#CCCCCC] rounded-[30px] text-[#484848]',
								}}
								labelProps={{
									children: 'Password',
									htmlFor: fields.password.id,
									className: 'text-[16px] text-[#32302F]',
								}}
								errorClass={'min-h-auto md:px-0 md:pt-0 '}
								errors={fields.password.errors}
							/>
							<div className="mt-[14px] text-[#32302F]">
								<Link
									to={authConfig.forgotPasswordPath}
									className="text-[14px] font-medium text-[#32302F]"
								>
									Forgot Password?
								</Link>
							</div>
							<div className="mt-[30px] flex items-center gap-3">
								<Checkbox
									id="terms"
									className="h-[30px] w-[30px] rounded-[10px] border-[#CCCCCC]"
								/>
								<label htmlFor="terms" className="text-[14px] text-[#32302F]">
									I agree to the{' '}
									<Link
										to="/terms"
										target="_blank"
										className="cursor-pointer font-medium underline"
									>
										Terms & Privacy
									</Link>
								</label>
							</div>
							<animated.div style={errorSpring} className="mt-6 mb-2">
								<ErrorList errors={form.errors} id={form.errorId} />
							</animated.div>
							<AnimatedStatusButton
								className="mt-[50px] mb-[30px] h-[55px] w-full rounded-[30px] border-0 bg-[#32302F] py-[18px] text-[20px] text-[#FFFFFF] capitalize transition-colors duration-200 hover:bg-[#1a1918] hover:text-[#FFFFFF] active:bg-[#0f0f0e] md:py-[23px]"
								status={isPending ? 'pending' : (form.status ?? 'idle')}
								type="submit"
								disabled={isPending}
								variant={'outline'}
							>
								log in
							</AnimatedStatusButton>
							{/* <p className="mt-8 mb-6 text-center text-[14px] font-normal text-[#32302F] md:text-[16px]">
								Don't have an account?
								<span
									className="text-[14px] font-normal text-[#32302F] md:text-[16px]"
									aria-hidden
								>
							
								</span>{' '}
								<Link
									to={signupLink}
									className="text-md font-500 text-[14px] font-medium text-black underline md:text-[16px]"
								>
									Create an Account
								</Link>
							</p> */}
						</StaggerChildren>
					</Form>
					<AnimatedContainer delay={0.3}>
						<AuthProviders
							type="Login"
							redirectTo={redirectTo}
							userType="landlord"
						/>
					</AnimatedContainer>
				</AnimatedContainer>
			</AuthFormWrapper>
		</AuthLayout>
	)
}

export const meta: Route.MetaFunction = () => {
	return [{ title: 'Login to TrueTenant - Landlord' }]
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
